import { CustomTranslations, IntlProvider, LanguageCodes } from "@ory/elements";
import { colorsCnam as colors, screenSize } from "@thetreep/ui";
import { ReactNode } from "react";
import styled from "styled-components";
import { customTranslations } from "@pages/authentication/translations";
// required styles for Ory Elements
import "@ory/elements/style.css";
import googleIcon from "@/assets/icons/google.png";
import microsoftIcon from "@/assets/icons/microsoft.png";
import { useTranslation } from "react-i18next";

// Wrapper for all authentication related components
// Will inject custom styles and translations
export function Authentication({
  children,
  type,
}: {
  children: ReactNode;
  type?: string;
}) {
  const { i18n } = useTranslation();
  return (
    <IntlProvider<CustomTranslations>
      locale={i18n.language as (typeof LanguageCodes)[number]}
      defaultLocale="en"
      customTranslations={customTranslations}
    >
      <div
        className={`flex w-full h-full items-center justify-center ${
          type === "settings" ? "" : "py-12"
        }`}
      >
        <AuthenticationCard type={type}>{children}</AuthenticationCard>
      </div>
    </IntlProvider>
  );
}

const AuthenticationCard = styled.div<{ type?: string }>`
  --ory-theme-success-emphasis: ${colors.secondary};
  max-width: ${(props) => (props.type === "settings" ? "100%" : "520px")};
  display: inline-block;
  width: 100%;
  .authCard *,
  .settingCard *,
  .gdpr-card * {
    font-family: Roboto, system-ui, sans-serif;
  }
  .authCard {
    background-color: ${colors.white};
    padding: 24px;
    border-radius: 8px;
    border: 1px solid ${colors.neutral200};
    max-width: 100%;

    @media ${screenSize.mobile} {
      padding: 32px;
    }
  }
  .authCard,
  .settingCard {
    label {
      color: ${colors.textStrong};
      font-size: 12px;
      font-weight: bold;
      line-height: normal;
    }
    a {
      color: ${colors.textStrong};
      font-weight: bold;
      text-decoration: none;
    }
  }

  .settingsContainer {
    margin-bottom: 100px;
  }
  .settingCard {
    display: flex;
    flex-direction: column;
    gap: 40px;
  }
  .ory_elements__grid_gap_4__v30igv3 {
    gap: 8px;
  }
  .ory_elements__grid_gap_16__v30igv5 {
    gap: 16px;
  }
  .settingCard > div,
  .gdpr-card {
    gap: 24px;
    display: flex;
    flex-direction: column;
    background: white;
    padding: 32px;
    border-radius: 8px;
    border: 1px solid ${colors.neutral200};
    @media ${screenSize.tablet} {
      flex-direction: row;
    }
    h3 {
      font-size: 20px;
      font-weight: 600;
      font-family: "Jakarta", sans-serif !important;
      line-height: normal;
      min-width: 184px;
      text-align: left;
    }
    form {
      width: 100%;
      border: none;
      padding: 0;
    }
    button {
      display: block;
      margin-left: auto;
    }
  }

  .gdpr-card {
    margin-top: 40px;
  }

  h2 {
    font-size: 24px;
    font-weight: 600;
    text-align: left;
    font-family: "Jakarta", sans-serif !important;
  }
  h3 {
    margin: 0;
  }
  input {
    border: 1px solid ${colors.borderSoft};
    background-color: ${colors.white};
    border-radius: 8px;
    padding: 12px;
  }

  input[type="checkbox"]:checked {
    background: ${colors.primary};
  }

  input[type="checkbox"]:checked::before {
    content: "";
    display: block;
    width: 7px;
    height: 4px;
    border-left: 3px solid #fff;
    border-bottom: 3px solid #fff;
    transform: rotate(-45deg);
    position: relative;
    left: -5px;
  }

  button,
  a[data-testid="node/anchor/continue"] {
    background-color: ${colors.secondary};
    border-radius: 4px;
    padding: 12px 16px;
    color: ${colors.white} !important;
    border-radius: 8px;
    font-size: 16px;
    &:hover {
      background-color: ${colors.secondaryStrong} !important;
    }
    &:focus {
      background-color: ${colors.secondary};
    }
  }
  button[name="email"] {
    background-color: ${colors.white};
    border: 1px solid ${colors.secondary};
    border-radius: 36px;
    padding: 8px 16px;
    color: ${colors.secondary} !important;

    &:hover {
      background-color: ${colors.surfaceSoft} !important;
    }
    &:focus {
      background-color: ${colors.surfaceStrong} !important;
    }
  }
  button[name="provider"] {
    background-color: ${colors.white};
    color: ${colors.black} !important;
    border: 1px solid ${colors.grey};
    &:hover {
      background-color: ${colors.primary10};
    }

    i {
      width: 20px;
      height: 20px;
      background-size: contain;
      background-position-x: center;
      background-position-y: center;
      background-repeat: no-repeat;
    }

    .fa-google {
      background-image: url("${googleIcon}");
    }

    .fa-microsoft {
      background-image: url("${microsoftIcon}");
    }
  }

  a[data-testid="node/anchor/continue"] {
    width: 100%;
    text-align: center;
    padding: 16px;
  }

  a {
    text-decoration: underline;
  }
  hr {
    display: none;
  }
  .ory_elements__message_severity_success__5sut1r5 {
    color: ${colors.primary};
  }
`;
